import * as Sentry from '@sentry/react';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { initAmplify } from '@/amplify/init-amplify';
import { App } from './app';

import '@fontsource/inter/300.css';
import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
import '@fontsource/inter/600.css';
import '@fontsource/inter/800.css';
import '@aws-amplify/ui-react/styles.css';
import './index.css';

/**
 * Strip anything that looks like an IPv4 dotted-quad or an IPv6
 * address from log attribute values. WebRTC logs intentionally omit
 * raw candidate addresses, but this is a defence-in-depth catch so a
 * future contributor who adds an IP to a structured log doesn't
 * accidentally ship user PII to Sentry.
 */
const IP_PATTERN =
  /\b(?:\d{1,3}\.){3}\d{1,3}\b|\b(?:[a-f0-9]{1,4}:){2,7}[a-f0-9]{1,4}\b/i;

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || 'development',
  sendDefaultPii: true,
  enableLogs: true,
  enabled: !!import.meta.env.VITE_SENTRY_DSN,
  integrations: [
    Sentry.consoleLoggingIntegration({ levels: ['warn', 'error'] }),
  ],
  beforeSendLog: (log) => {
    if (log.attributes) {
      for (const [key, value] of Object.entries(log.attributes)) {
        if (typeof value === 'string' && IP_PATTERN.test(value)) {
          log.attributes[key] = '[redacted-ip]';
        }
      }
    }
    return log;
  },
});

initAmplify();

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
